import { History } from "lucide-react"
import type { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { SubmitButton } from "@/components/app/form"
import { Shortcut } from "@/components/app/kbd"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { type AgentFormValues, dirtyKeys, hasTrackedChanges } from "@/features/studio/agent-form"
import { cn } from "@/lib/utils"

/**
 * Sticky bar for the agent editor: what changed, an optional revision note
 * (only when the change creates a revision) and Discard / Save.
 */
export function SaveBar({
  form,
  note,
  onNoteChange,
  onDiscard,
}: {
  form: UseFormReturn<AgentFormValues>
  note: string
  onNoteChange: (v: string) => void
  onDiscard: () => void
}) {
  const { t } = useTranslation()
  const { isDirty, isSubmitting, dirtyFields } = form.formState
  const keys = dirtyKeys(dirtyFields)
  const tracked = hasTrackedChanges(dirtyFields)
  const invalid = Object.keys(form.formState.errors).length > 0
  const show = isDirty && keys.length > 0
  return (
    <div
      aria-hidden={!show}
      className={cn(
        "pointer-events-none sticky bottom-[calc(1rem+env(safe-area-inset-bottom))] z-20 mt-6 flex justify-center transition-all duration-200 max-md:bottom-[calc(4.5rem+env(safe-area-inset-bottom))]",
        show ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
      )}
    >
      <div className="bg-popover/95 supports-backdrop-filter:bg-popover/80 pointer-events-auto flex w-full max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-2.5 shadow-lg backdrop-blur">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{t("form.unsaved")}</div>
          <div className="text-muted-foreground truncate text-xs">
            {invalid
              ? t("studio.agents.save.invalid")
              : keys.map((k) => t(`studio.agents.save.fields.${k}`)).join(" · ")}
          </div>
        </div>
        {tracked && (
          <div className="order-last flex w-full items-center gap-1.5">
            <History className="text-muted-foreground size-3.5 shrink-0" />
            <Input
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder={t("studio.agents.save.notePlaceholder")}
              aria-label={t("studio.agents.save.note")}
              className="h-7 text-xs"
              maxLength={255}
              tabIndex={show ? 0 : -1}
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onDiscard}
            disabled={isSubmitting || !isDirty}
            tabIndex={show ? 0 : -1}
          >
            {t("form.discard")}
          </Button>
          <SubmitButton
            form={form}
            size="sm"
            disabled={!isDirty}
            tabIndex={show ? 0 : -1}
            data-testid="save-agent"
          >
            {t("form.saveChanges")}
            <Shortcut keys={["mod", "S"]} className="ml-1 max-sm:hidden" />
          </SubmitButton>
        </div>
      </div>
    </div>
  )
}
